import { useRef } from "react";
import { Box } from "@mui/material";
import Image from "@tiptap/extension-image";
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from "@tiptap/react";

const MIN_WIDTH = 40;

// An image (on its own line) whose width the writer can drag from its corner handle. The
// width is stored on the node and sent as the <img width> attribute (plus a
// matching style), which mail clients honour.
function ImageView({ node, updateAttributes, selected, editor }: NodeViewProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const width = node.attrs.width as number | null;

  const startDrag = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const img = imgRef.current;
    if (!img) return;
    const startX = e.clientX;
    const startWidth = img.getBoundingClientRect().width;
    const maxWidth = (editor.view.dom as HTMLElement).clientWidth;
    const onMove = (ev: PointerEvent) => {
      const next = Math.round(Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + ev.clientX - startX)));
      updateAttributes({ width: next });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <NodeViewWrapper as="div" style={{ margin: "4px 0" }}>
      <span style={{ display: "inline-block", position: "relative", lineHeight: 0, maxWidth: "100%" }}>
        <img
          ref={imgRef}
          src={node.attrs.src as string}
          alt={(node.attrs.alt as string) ?? ""}
          width={width ?? undefined}
          style={{ maxWidth: "100%", height: "auto", width: width ? `${width}px` : undefined }}
          data-drag-handle
          draggable
        />
        {selected && editor.isEditable && (
          <Box
            onPointerDown={startDrag}
            title="Drag to resize"
            sx={{
              position: "absolute", right: -5, bottom: -5, width: 12, height: 12,
              bgcolor: "primary.main", border: "2px solid #fff", borderRadius: "2px",
              cursor: "nwse-resize", boxShadow: 1,
            }}
          />
        )}
      </span>
    </NodeViewWrapper>
  );
}

export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => {
          const w = parseInt(el.getAttribute("width") ?? "", 10);
          return Number.isFinite(w) ? w : null;
        },
        renderHTML: (attrs) => (attrs.width
          ? { width: String(attrs.width), style: `width:${attrs.width}px;max-width:100%;height:auto` }
          : {}),
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageView);
  },
});
